import api from "./apiService";

/**
 * Fetch notifications for the current user
 * @param {Object} options - Options for the fetch request
 * @param {number} options.limit - Maximum number of notifications to fetch (default: 20)
 * @param {number} options.offset - Offset for pagination (default: 0)
 * @param {boolean} options.unreadOnly - Whether to fetch only unread notifications (default: false)
 * @returns {Promise<Object>} Notifications data with total and unread counts
 */
const fetchNotifications = async (options = {}) => {
    try {
        const params = new URLSearchParams();
        
        // Add pagination parameters
        if (options.limit) {
            params.append('limit', options.limit);
        }
        
        if (options.offset) {
            params.append('offset', options.offset);
        }
        
        // Add filter for unread notifications
        if (options.unreadOnly) {
            params.append('unread_only', 'true');
        }
        
        const url = `/notifications?${params.toString()}`;
        
        const response = await api.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Get count of unread notifications
 * @returns {Promise<Object>} Object containing unread_count
 */
const getUnreadNotificationCount = async () => {
    try {
        const response = await api.get('/notifications/unread-count');
        return response.data;
    } catch (error) {
        // Return 0 as default if there's an error
        return { unread_count: 0 };
    }
};

/**
 * Mark a notification as read
 * @param {number} notificationId - The ID of the notification to mark as read
 * @returns {Promise<Object>} Response data
 */
const markNotificationAsRead = async (notificationId) => {
    try {
        const response = await api.put(`/notifications/${notificationId}/read`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Mark all notifications as read
 * @returns {Promise<Object>} Response data
 */
const markAllNotificationsAsRead = async () => {
    try {
        const response = await api.put('/notifications/read-all');
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Delete a notification
 * @param {number} notificationId - The ID of the notification to delete
 * @returns {Promise<Object>} Response data
 */
const deleteNotification = async (notificationId) => {
    try {
        const response = await api.delete(`/notifications/${notificationId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Create a notification service object with all the functions
const notificationService = {
    fetchNotifications,
    getUnreadNotificationCount,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    deleteNotification
};

// Export both the service object as default and individual functions
export default notificationService;
export {
    fetchNotifications,
    getUnreadNotificationCount,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    deleteNotification
};